import { CareHistoryDetail } from "@/components/user-record/care/care-history-detail";
import { PresentationReviewBody } from "@/components/user-record/care/presentation-review-card";
import {
  useCareHistoryDetail,
  useCarePage,
  useCareSnapshot,
  type CareHistoryDetail as SubmittedDetail,
} from "@/lib/api/care";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

export function CareReviewDetail({
  reviewId,
  submitted,
  menteeId,
  menteeName,
  adminId,
  onSubmitted,
  onDraftSaved,
}: {
  reviewId: string;
  submitted: boolean;
  menteeId: string;
  menteeName: string;
  adminId: string | undefined;
  onSubmitted: () => void;
  onDraftSaved: () => void;
}) {
  const { data: page, isPending, isError } = useCarePage(menteeId);
  const submittedReview = useCareHistoryDetail(
    menteeId,
    "presentation",
    reviewId,
    submitted,
  );
  const snapshot = useCareSnapshot(menteeId, "all", false);
  const engaged = snapshot.data?.engagement_days;

  return (
    <section className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#dfddd8] bg-white">
      <header className="flex min-h-[88px] shrink-0 flex-col justify-center gap-1 bg-[#e5e0d2] px-6 py-2">
        <div className="flex items-baseline gap-2">
          <h2 className="font-heading text-2xl font-bold leading-8 text-[#0e0d0c]">
            {menteeName}
          </h2>
          <Link
            to="/users/$userId"
            params={{ userId: menteeId }}
            search={{ tab: "care", from: "care-reviews" }}
            className="font-body text-xs leading-4 text-[#58534b] underline"
            onClick={() =>
              trackEvent(PostHogEvents.CARE_REVIEWS_OPEN_CARE_PAGE, {
                admin_id: adminId,
                teen_id: menteeId,
              })
            }
          >
            View care history
          </Link>
        </div>
        <p className="font-body text-xs leading-4 text-[#58534b]">
          Mentored by{" "}
          <span className="text-[#0e0d0c]">{page?.mentor?.name ?? "—"}</span>
          {" | "}
          <span className="font-bold text-[#0e0d0c]">
            {page ? page.days_on_platform : "—"} days
          </span>{" "}
          in mentorship
          {" | "}
          <span className="font-bold text-[#0e0d0c]">
            {engaged ?? "—"} days
          </span>{" "}
          engaged
        </p>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {submitted ? (
          <SubmittedReview
            pending={submittedReview.isPending}
            detail={submittedReview.data}
            error={submittedReview.isError}
          />
        ) : isPending ? (
          <ReviewSpinner />
        ) : isError || !page ? (
          <ReviewError />
        ) : (
          <PresentationReviewBody
            menteeId={menteeId}
            page={page}
            expanded
            onSubmitted={onSubmitted}
            onDraftSaved={onDraftSaved}
          />
        )}
      </div>
    </section>
  );
}

function SubmittedReview({
  pending,
  detail,
  error,
}: {
  pending: boolean;
  detail: SubmittedDetail | undefined;
  error: boolean;
}) {
  if (pending) return <ReviewSpinner />;
  if (error || !detail) return <ReviewError />;
  return (
    <div className="px-6 py-4">
      <CareHistoryDetail detail={detail} />
    </div>
  );
}

function ReviewSpinner() {
  return (
    <div
      className="flex h-full items-center justify-center"
      role="status"
      aria-label="Loading review"
    >
      <Loader2 className="size-6 animate-spin text-[#a0998d]" />
    </div>
  );
}

function ReviewError() {
  return (
    <p className="px-6 py-8 font-body text-sm text-states-error">
      Unable to load this review.
    </p>
  );
}
